// Textos sugeridos para a Justificativa SOX de cada ação do Workflow. A tela já abre o campo preenchido com
// o texto da ação (citando grupo, contas, empresas etc.); a pessoa revisa e edita antes de confirmar, e o
// texto confirmado é o que vai para o registro SOX. Funções puras, sem React.

const limpar = (v?: string | null) => (v ?? '').trim();

/** "a; b; c e mais 2" — mostra só os primeiros itens para o texto não ficar enorme. */
export const resumirLista = (itens: string[], max = 5): string =>
  itens.length <= max ? itens.join('; ') : `${itens.slice(0, max).join('; ')} e mais ${itens.length - max}`;

const trecho = (rotulo: string, grupo?: string | null, subgrupo?: string | null) => {
  const g = limpar(grupo);
  const s = limpar(subgrupo);
  if (!g) return `${rotulo} a definir`;
  return s ? `${rotulo} "${g}", subgrupo "${s}"` : `${rotulo} "${g}"`;
};

export interface ContaResumo { codigo: string; descricao: string; grupoConta?: string | null }

const rotuloConta = (c: ContaResumo) => `${c.codigo} - ${c.descricao}`;

export const justificativaDeParaIndividual = (p: { conta: ContaResumo; grupo?: string; subgrupo?: string }): string =>
  `Classificação da conta ${rotuloConta(p.conta)} no ${trecho('grupo De/Para', p.grupo, p.subgrupo)}, ` +
  'conforme a natureza e a finalidade da conta no plano de contas (ECD).';

export const justificativaDeParaLote = (p: { contas: ContaResumo[]; grupo?: string; subgrupo?: string; apenasClientes?: boolean }): string => {
  const n = p.contas.length;
  const categorias = Array.from(new Set(p.contas.map(c => limpar(c.grupoConta)).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const criterio = categorias.length > 0
    ? `Critério: contas classificadas no ECD como ${resumirLista(categorias.map(c => `"${c}"`), 4)}`
    : 'Critério: contas selecionadas pela afinidade com o grupo';
  const clientes = p.apenasClientes ? ', todas contas de Clientes' : '';
  const contas = n > 0 ? ` Contas: ${resumirLista(p.contas.map(rotuloConta), 5)}.` : '';
  return `Mapeamento em lote de ${n} ${n === 1 ? 'conta contábil' : 'contas contábeis'} para o ${trecho('grupo De/Para', p.grupo, p.subgrupo)}. ` +
    `${criterio}${clientes}, com natureza e finalidade compatíveis com o grupo.${contas}`;
};

export const justificativaRenomearGrupo = (grupoAtual: string): string =>
  `Renomeação do grupo De/Para "${limpar(grupoAtual)}" para padronização da nomenclatura. ` +
  'As contas vinculadas e seus subgrupos permanecem os mesmos.';

export const justificativaComposicaoGrupo = (p: { grupo: string; incluidas: string[]; removidas: string[] }): string => {
  const base = `Ajuste na composição do grupo De/Para "${limpar(p.grupo)}"`;
  const partes: string[] = [];
  if (p.incluidas.length > 0) partes.push(`inclusão de ${p.incluidas.length} ${p.incluidas.length === 1 ? 'conta' : 'contas'} (${resumirLista(p.incluidas, 4)})`);
  if (p.removidas.length > 0) partes.push(`remoção de ${p.removidas.length} ${p.removidas.length === 1 ? 'conta' : 'contas'} (${resumirLista(p.removidas, 4)})`);
  return partes.length === 0
    ? `Revisão da composição do grupo De/Para "${limpar(p.grupo)}".`
    : `${base}: ${partes.join(' e ')}. A remoção desfaz apenas o vínculo com o grupo; a conta contábil e seus lançamentos não são alterados.`;
};

export const justificativaNovoGrupoEconomico = (nome?: string): string =>
  `Criação do Grupo Econômico${limpar(nome) ? ` "${limpar(nome)}"` : ''} considerando todas as empresas do Grupo.`;

export const justificativaEdicaoGrupoEconomico = (nome?: string): string =>
  `Atualização do cadastro do grupo econômico ${limpar(nome) ? `"${limpar(nome)}"` : 'selecionado'}.`;

export const justificativaVinculoEmpresaGrupo = (p: { empresa?: string; grupo?: string }): string =>
  `Vínculo da empresa ${limpar(p.empresa) ? `"${limpar(p.empresa)}"` : 'selecionada'} ao grupo econômico ` +
  `${limpar(p.grupo) ? `"${limpar(p.grupo)}"` : 'selecionado'}, para que passe a seguir as alçadas e regras de aprovação do grupo.`;

export const justificativaAcessos = (p: { usuarios: string[]; empresas: string[] }): string => {
  const usuarios = p.usuarios.length > 0 ? resumirLista(p.usuarios, 4) : 'usuários selecionados';
  const empresas = p.empresas.length > 0 ? resumirLista(p.empresas, 4) : 'empresas selecionadas';
  return `Concessão de acesso de ${usuarios} às empresas ${empresas}, conforme a atribuição de responsabilidades no processo de ` +
    'conciliação e aprovação SOX.';
};

export const justificativaVinculoResponsavel = (p: { qtdContas: number; modo: 'grupo' | 'usuario'; responsavel?: string }): string => {
  const contas = `${p.qtdContas} ${p.qtdContas === 1 ? 'conta contábil' : 'contas contábeis'}`;
  const quem = limpar(p.responsavel);
  const destino = p.modo === 'grupo'
    ? `ao cargo/grupo ${quem ? `"${quem}"` : 'selecionado'}`
    : `ao usuário ${quem ? `"${quem}"` : 'selecionado'}`;
  return `Designação de responsável SOX: vínculo de ${contas} ${destino}, de acordo com a segregação de funções e a matriz de responsabilidades.`;
};

export const justificativaAlcada = (p: { nivel: number; escopo: string; minimo?: string; maximo?: string; slaDias?: string }): string => {
  const faixa = limpar(p.minimo) || limpar(p.maximo)
    ? ` Faixa de valor de R$ ${limpar(p.minimo) || '0'} a R$ ${limpar(p.maximo) || 'sem limite'}.`
    : '';
  const sla = limpar(p.slaDias) ? ` Prazo (SLA) de ${limpar(p.slaDias)} dia(s) útil(eis).` : '';
  return `Configuração da alçada do nível N${p.nivel} (escopo: ${p.escopo}).${faixa}${sla} Definida conforme a política de aprovação e a segregação de funções.`;
};

export const justificativaCalendarioFiscal = (ano: number | string): string =>
  `Sincronização do calendário fiscal de ${ano} (feriados nacionais, BrasilAPI) para o cálculo de prazos de SLA em dias úteis.`;
